import type { LiquidityPool } from "features/wallet/api/Wallet";
import {
    getStatementPaymentPools,
    suggestPoolsForImport,
} from "features/budget";
import type { BudgetTransaction } from "types/BudgetTransaction";

const CARD_POOL_KEY = "cardPaymentPool";
const GLOBAL_POOL_KEY = "cardPaymentPoolGlobal";

function storageKey(prefix: string, username: string, suffix?: string): string {
    return suffix ? `${prefix}:${username}:${suffix}` : `${prefix}:${username}`;
}

function readPoolMap(key: string): Record<string, string> {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return {};
        const parsed = JSON.parse(raw) as Record<string, string>;
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
        return {};
    }
}

function writePoolMap(key: string, value: Record<string, string>): void {
    localStorage.setItem(key, JSON.stringify(value));
}

export function getCardPaymentPoolDefaults(username: string, cardLabel: string): Record<string, string> {
    return readPoolMap(storageKey(CARD_POOL_KEY, username, cardLabel.trim()));
}

export function getGlobalCardPaymentPoolDefaults(username: string): Record<string, string> {
    return readPoolMap(storageKey(GLOBAL_POOL_KEY, username));
}

export function persistCardPaymentPoolDefaults(
    username: string,
    cardLabel: string,
    poolByCurrency: Record<string, string>,
): void {
    const normalized = Object.fromEntries(
        Object.entries(poolByCurrency)
            .filter(([, poolId]) => Boolean(poolId))
            .map(([currency, poolId]) => [currency.toUpperCase(), poolId]),
    );
    if (Object.keys(normalized).length === 0) return;
    writePoolMap(storageKey(CARD_POOL_KEY, username, cardLabel.trim()), normalized);
    writePoolMap(storageKey(GLOBAL_POOL_KEY, username), {
        ...getGlobalCardPaymentPoolDefaults(username),
        ...normalized,
    });
}

export function resolveDefaultPoolsForCardPayment({
    username,
    cardLabel,
    currencies,
    pools,
    importLines,
}: {
    username: string;
    cardLabel: string;
    currencies: string[];
    pools: LiquidityPool[];
    importLines: BudgetTransaction[];
}): Record<string, string> {
    const fromLines = suggestPoolsForImport(importLines);
    const fromStatement = getStatementPaymentPools(importLines);
    const fromCard = getCardPaymentPoolDefaults(username, cardLabel);
    const fromGlobal = getGlobalCardPaymentPoolDefaults(username);

    const result: Record<string, string> = {};
    for (const currency of currencies) {
        const normalized = currency.toUpperCase();
        const poolsForCurrency = pools.filter((pool) => pool.currency === normalized);
        const singlePoolId = poolsForCurrency.length === 1 ? poolsForCurrency[0]._id : "";

        result[normalized] =
            fromLines[normalized]
            || fromStatement[normalized]
            || fromCard[normalized]
            || fromGlobal[normalized]
            || singlePoolId
            || "";
    }
    return result;
}

export function hasConfidentPoolDefaults(poolByCurrency: Record<string, string>, currencies: string[]): boolean {
    return currencies.every((currency) => Boolean(poolByCurrency[currency.toUpperCase()]));
}
